import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, Minus, Plus, Ticket, Lock } from "lucide-react";
import { toast } from "sonner";
import customerApi from "../../hq/customerApi";

const nz = (n) => `NZ$${Number(n || 0).toFixed(2)}`;

export function LowStockPill({ remaining, soldOut, className = "" }) {
  if (soldOut) return <span className={`text-[10px] font-bold uppercase tracking-[0.2em] text-[#A33030] ${className}`} data-testid="ticket-sold-out">Sold out</span>;
  if (remaining === null || remaining === undefined) return null;
  return <span className={`inline-flex items-center gap-1 rounded-full bg-[#FBE7C6] text-[#8B4A0B] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${className}`} data-testid="ticket-selling-fast">
    ✦ Selling fast · {remaining} left
  </span>;
}

export default function TicketPicker({ event }) {
  const t = event.tickets || {};
  const loc = useLocation();
  const nav = useNavigate();
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const loggedIn = !!localStorage.getItem("heritage-customer-token");

  const total = useMemo(() => (t.types || []).reduce((s, ty) => s + (qty[ty.id] || 0) * ty.price, 0), [qty, t.types]);
  const count = Object.values(qty).reduce((s, n) => s + n, 0);
  const maxPer = t.max_per_order || 10;

  const bump = (ty, d) => {
    const cur = qty[ty.id] || 0;
    let next = Math.max(0, cur + d);
    if (d > 0 && count >= maxPer) return toast.error(`Up to ${maxPer} tickets per order.`);
    if (d > 0 && ty.remaining !== null && ty.remaining !== undefined && next > ty.remaining) return toast.error(`Only ${ty.remaining} ${ty.name} ticket${ty.remaining === 1 ? "" : "s"} left.`);
    if (d > 0 && t.remaining !== null && t.remaining !== undefined && count + 1 > t.remaining) return toast.error(`Only ${t.remaining} ticket${t.remaining === 1 ? "" : "s"} left for this Open Day.`);
    setQty({ ...qty, [ty.id]: next });
  };

  const buy = async () => {
    if (!loggedIn) { toast("Sign in to Heritage HQ to book tickets."); return nav("/account/login", { state: { from: loc.pathname } }); }
    setBusy(true);
    try {
      const lines = Object.entries(qty).filter(([, n]) => n > 0).map(([type_id, n]) => ({ type_id, qty: n }));
      const r = await customerApi.post(`/visit/open-days/${event.slug}/tickets/checkout`, { lines, origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
      else nav(`/visit/tickets/${r.data.order_id}`);
    } catch (err) {
      if (err?.response?.status === 401) return nav("/account/login", { state: { from: loc.pathname } });
      toast.error(err?.response?.data?.detail || "Couldn't start your booking , please try again.");
    } finally { setBusy(false); }
  };

  if (!t.open) return null;
  if (t.sold_out) return (
    <div className="mt-8 rounded-[24px] border-[3px] border-[#A33030]/40 bg-white p-6 text-center" data-testid="ticket-picker-sold-out">
      <div className="font-display text-2xl text-[#A33030]">Sold out</div>
      <p className="text-sm text-[var(--dahlias-ink)]/70 mt-1">Every ticket for this Open Day has gone. Keep an eye on HQ , we'll announce any released spots there first.</p>
    </div>
  );

  return (
    <div className="mt-8 rounded-[24px] border-[3px] border-[var(--berry)] bg-white p-5 sm:p-6" data-testid="ticket-picker">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-2xl text-[var(--dahlias-ink)] flex items-center gap-2"><Ticket className="w-5 h-5 text-[var(--berry)]" /> Tickets</h2>
        <LowStockPill remaining={t.remaining} />
      </div>
      <ul className="mt-4 divide-y divide-[var(--berry)]/15">
        {t.types.map((ty) => (
          <li key={ty.id} className="py-3 flex items-center justify-between gap-3" data-testid={`ticket-type-${ty.id}`}>
            <div>
              <div className="font-semibold text-[var(--dahlias-ink)]">{ty.name} <span className="text-[var(--berry)] ml-1">{ty.price > 0 ? nz(ty.price) : "Free"}</span></div>
              {ty.description && <div className="text-xs text-[var(--dahlias-ink)]/65">{ty.description}</div>}
              {!ty.available && <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A33030]">Sold out</div>}
              {ty.available && ty.remaining !== null && ty.remaining !== undefined && <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B4A0B]">{ty.remaining} left</div>}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => bump(ty, -1)} disabled={!qty[ty.id]} className="w-9 h-9 rounded-full border-2 border-[var(--berry)]/40 text-[var(--berry)] disabled:opacity-30 inline-flex items-center justify-center" data-testid={`ticket-minus-${ty.id}`}><Minus className="w-4 h-4" /></button>
              <span className="w-6 text-center font-semibold" data-testid={`ticket-qty-${ty.id}`}>{qty[ty.id] || 0}</span>
              <button type="button" onClick={() => bump(ty, 1)} disabled={!ty.available} className="w-9 h-9 rounded-full bg-[var(--berry)] text-white disabled:opacity-30 inline-flex items-center justify-center" data-testid={`ticket-plus-${ty.id}`}><Plus className="w-4 h-4" /></button>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t-2 border-[var(--berry)]/20 pt-4">
        <div>
          <div className="font-label text-[9px] tracking-[0.3em] uppercase text-[var(--berry)]">Total</div>
          <div className="font-display text-2xl text-[var(--dahlias-ink)]" data-testid="ticket-total">{nz(total)}</div>
          <div className="text-[11px] text-[var(--dahlias-ink)]/60">{count} ticket{count === 1 ? "" : "s"} · secure card payment via Stripe</div>
        </div>
        <button type="button" onClick={buy} disabled={busy || count === 0} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--berry)] text-white font-label text-xs tracking-[0.3em] uppercase disabled:opacity-40" data-testid="ticket-buy">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : !loggedIn ? <Lock className="w-4 h-4" /> : <Ticket className="w-4 h-4" />}
          {loggedIn ? (total > 0 ? "Pay & book" : "Book free tickets") : "Sign in to book"}
        </button>
      </div>
      {!loggedIn && <p className="mt-3 text-xs text-[var(--dahlias-ink)]/60">Tickets live in your Heritage HQ account so they're always to hand at the gate.</p>}
    </div>
  );
}
